import React, { useEffect, useMemo, useRef, useState } from "react";
import { PowerBIEmbed } from "powerbi-client-react";
import { models } from "powerbi-client";
import { useParams, useNavigate } from "react-router-dom";
import $ from "jquery";
import { FaPrint, FaFilePowerpoint } from "react-icons/fa";
import { MdOutlineAccessTimeFilled, MdRefresh } from "react-icons/md";
import { Box, Fab, TextField, Tooltip, Button, Select, MenuItem, FormControl, CircularProgress } from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import NavHeader from "../nav/NavHeader";
import SideMenu from "../nav/Sidemenu";
import ReportChatBot from "./ReportChatBot";
import Login from "../../pages/Login";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import { useCustomVisuals } from "../../hooks/useCustomVisuals";
import { useBookmark } from "../../hooks/useBookmark";
import { useReportTracking } from "../../hooks/useReportTracking";
import { useUserAssignedPages } from "../../context/UserAssignedPagesContext";
import { getToken } from "../auth/authConfig";
import AppButton from "../layout/AppButton";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BarChartLoader from "../layout/BarChartLoader";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import { apiService } from "../../services/apiClient";
import { apiUrl } from "../../services/apiUrl";

// Fancy glassmorphism-style loader shown full-bleed over the report area while
// it loads / the AI assistant data is being prepared
const FancyReportLoader = ({ message }) => (
  <div className="absolute inset-0 flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#fdf4ff] via-white to-[#fdf2f8]">
    <style>{`
      @keyframes reportLoaderShimmer { 0% { background-position: 0% 50%; } 100% { background-position: 200% 50%; } }
      @keyframes reportLoaderProgress { 0% { transform: translateX(-100%); } 100% { transform: translateX(300%); } }
      @keyframes reportLoaderRingSpin { to { transform: rotate(360deg); } }
      @keyframes reportLoaderRingSpinReverse { to { transform: rotate(-360deg); } }
      @keyframes reportLoaderBlobFloat {
        0%, 100% { transform: translate(0, 0) scale(1); }
        50% { transform: translate(24px, -18px) scale(1.1); }
      }
    `}</style>

    {/* floating blobs */}
    <div
      aria-hidden="true"
      className="absolute -top-16 -left-16 h-64 w-64 rounded-full bg-[color-mix(in_srgb,var(--app-icon-color,#bb1ccc)_18%,transparent)] blur-[70px]"
      style={{ animation: "reportLoaderBlobFloat 8s ease-in-out infinite" }}
    />
    <div
      aria-hidden="true"
      className="absolute -bottom-16 -right-16 h-72 w-72 rounded-full bg-[color-mix(in_srgb,var(--app-ai-assistant-secondary,#5e0768)_18%,transparent)] blur-[80px]"
      style={{ animation: "reportLoaderBlobFloat 9s ease-in-out infinite reverse" }}
    />

    {/* glass card */}
    <div className="relative z-10 flex flex-col items-center gap-5 rounded-3xl border border-white/70 bg-white/70 px-12 py-10 shadow-2xl backdrop-blur-xl">
      {/* dual counter-rotating gradient rings around a chart icon */}
      <div className="relative h-24 w-24">
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: "conic-gradient(from 0deg, transparent 0%, var(--app-icon-color,#bb1ccc) 42%, transparent 55%)",
            animation: "reportLoaderRingSpin 1.3s linear infinite",
          }}
        />
        <div
          className="absolute inset-0 rounded-full opacity-70"
          style={{
            background: "conic-gradient(from 180deg, transparent 0%, var(--app-ai-assistant-secondary,#5e0768) 32%, transparent 48%)",
            animation: "reportLoaderRingSpinReverse 2s linear infinite",
          }}
        />
        <div className="absolute inset-[6px] flex items-center justify-center rounded-full bg-white shadow-inner">
          <InsertChartOutlinedIcon style={{ fontSize: 34, color: "var(--app-icon-color, #bb1ccc)" }} />
        </div>
      </div>

      {/* shimmering gradient text */}
      <p
        className="text-center text-base font-bold"
        style={{
          background:
            "linear-gradient(90deg, #334155, var(--app-icon-color,#bb1ccc), var(--app-ai-assistant-secondary,#5e0768), #334155)",
          backgroundSize: "200% auto",
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          color: "transparent",
          animation: "reportLoaderShimmer 3s linear infinite",
        }}
      >
        {message}
      </p>

      {/* indeterminate progress bar */}
      <div className="h-1.5 w-56 overflow-hidden rounded-full bg-slate-200">
        <div
          className="h-full w-1/3 rounded-full"
          style={{
            background: "var(--app-ai-assistant-gradient, linear-gradient(135deg,#bb1ccc,#5e0768))",
            animation: "reportLoaderProgress 1.4s ease-in-out infinite",
          }}
        />
      </div>
    </div>
  </div>
);

/**
 * A stable key for a saved slice.
 *
 * Power BI does not guarantee the order of filters or of a slicer's values, and
 * it decorates them with fields like displaySettings, so the raw JSON of one
 * view differs run to run. Comparing raw text made a restored bookmark look
 * unbookmarked.
 *
 * Only inclusions count: a page's own pane filters ("Brand is not DELETED") are
 * report hygiene, are reported inconsistently by getFilters(), and are not part
 * of what a user chose.
 */
function sliceSignature(filterJson, slicerJson) {
  const parts = [];

  const add = (filter) => {
    const table = filter?.target?.table;
    const column = filter?.target?.column;
    const values = filter?.values;
    if (filter?.operator !== "In") return;
    if (!table || !column || !Array.isArray(values) || values.length === 0) return;
    parts.push(`${table}.${column}|${values.map(String).sort().join(",")}`);
  };

  try {
    if (filterJson) JSON.parse(filterJson).forEach(add);
  } catch {
    // A malformed record simply contributes nothing.
  }

  try {
    if (slicerJson) {
      JSON.parse(slicerJson).forEach((slicer) => (slicer?.state?.filters || []).forEach(add));
    }
  } catch {
    // As above.
  }

  return [...new Set(parts)].sort().join(";");
}

const PowerBiReports = () => {
  const { fetchReportData } = useCustomVisuals();
  const { fetchBookmarks, saveBookmark, deleteBookmark } = useBookmark();
  const { trackReportView } = useReportTracking();
  const { features: featureFlags, ensureLoaded } = useUserAssignedPages();
  const navigate = useNavigate();

  useEffect(() => {
    ensureLoaded().catch(() => {});
  }, [ensureLoaded]);

  const [pbireports, setPbireports] = useState([]);
  const [isLoading, setIsloading] = useState(false);
  const [iserror, seterror] = useState(false);
  const [isLoaded, seteLoaded] = useState(false);
  const [PageLoading, setPageLoading] = useState(true);
  const [currentPage, setcurrentPage] = useState(0);
  const [pagename, setPageName] = useState("");
  const [pageDisplayname, setDisplyName] = useState("");
  const [open, setOpen] = useState(false);
  const [openBookmark, setopenBookmark] = useState(false);

  const [pbiPages, setPages] = useState([]);
  const { ReportID } = useParams();

  const getURl = new URL(window.location.href);
  const reportnameFetch = getURl.searchParams.get("ReportSection");
  const bookmarkIdFromUrl = getURl.searchParams.get("bookmarkId");
  const groupIdFromUrl = getURl.searchParams.get("groupId");
  const [Saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  const [saveBookmarkReports, setsaveBookmarkReports] = useState([]);

  const { instance } = useMsal();

  // console.log(reportnameFetch);

  const [passtoken, setpasstoken] = useState(null);
  const reportContainerRef = useRef(null);
  const isExportingVisualsRef = useRef(false);
  // Mirrors isExportingVisualsRef but as state, so the loading overlay can react to it
  const [isExportingVisuals, setIsExportingVisuals] = useState(false);
  // Populated once after the report loads; the chatbot just reads from this instead
  // of re-exporting visuals (and flipping pages) on every question
  const [allVisualsData, setAllVisualsData] = useState([]);

  // Ask AI split-pane: report is 100% width when closed, and shares space with
  // a user-resizable panel (default 20%) when open.
  const [onboardingEnabled, setOnboardingEnabled] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatWidthPct, setChatWidthPct] = useState(30);
  const [isResizingChat, setIsResizingChat] = useState(false);
  const [isHoveringChatResize, setIsHoveringChatResize] = useState(false);
  const splitContainerRef = useRef(null);
  const isResizingChatRef = useRef(false);

  const handleChatResizeStart = (e) => {
    e.preventDefault();
    isResizingChatRef.current = true;
    setIsResizingChat(true);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizingChatRef.current || !splitContainerRef.current) return;
      const rect = splitContainerRef.current.getBoundingClientRect();
      const chatWidthPx = rect.right - e.clientX;
      const pct = Math.min(60, Math.max(15, (chatWidthPx / rect.width) * 100));
      setChatWidthPct(pct);
    };
    const handleMouseUp = () => {
      if (isResizingChatRef.current) {
        isResizingChatRef.current = false;
        setIsResizingChat(false);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      }
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchToken = async () => {
      try {
        const token = await getToken();
        if (token && isMounted) {
          setpasstoken(token);
        }
      } catch (error) {
        console.error("Failed to acquire token", error);
      }
    };
    fetchToken();
    return () => {
      isMounted = false;
    };
  }, [instance]);

  const viewNotificationsReply = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const notificationID = searchParams.get("notificationiD");

    if (notificationID !== null) {
      $("#NotificationsIdValues").css("display", "block");
      $("#main").css("marginRight", "400px");
      $(`#postDataid_${notificationID}`).css("display", "block");

      const element = document.getElementById(`topic_id_${notificationID}`);
      if (element) {
        element.scrollIntoView();
      } else {
        console.error(`Element with id 'topic_id_${notificationID}' not found`);
      }
    }
  };

  useEffect(() => {
    const fetchSearchByname = async () => {
      try {
        const json = await fetchReportData(ReportID);
        setPbireports(json.data);
        seterror(false);
        setIsloading(false);
        setPageLoading(false);
        const data = json.data;
        if (data.embedReport.embedToken === null) {
          seteLoaded(true);
        }
      } catch (err) {
        seterror(true);
        setPbireports(null);
        seteLoaded(false);
        setPageLoading(false);
      }
    };
    fetchSearchByname();
  }, [ReportID]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    GetBookmarkURLReports();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const GetBookmarkURLReports = async () => {
    try {
      const json = await fetchBookmarks();
      setsaveBookmarkReports(json);
    } catch (err) {
      setsaveBookmarkReports(null);
    }
  };

  if (pbireports === null) {
    console.log("Data not available");
  }

  const embeddingData = pbireports?.embedReport;
  const EmbedUrl = embeddingData?.url;
  const Embedaccess_token = embeddingData?.embedToken;
  const EmbedPBIreport_id = `${embeddingData?.reportGuid}`;
  const PowerBiReportsName = embeddingData?.name;

  useEffect(() => {
    if (!EmbedPBIreport_id || EmbedPBIreport_id === "undefined") return;
    let cancelled = false;
    apiService.get(apiUrl.onboardingChatStatus(EmbedPBIreport_id))
      .then((resp) => {
        if (cancelled) return;
        const enabled = resp?.data?.enabled ?? resp?.enabled ?? false;
        setOnboardingEnabled(enabled);
        try {
          const stored = localStorage.getItem(`chatOpen_${EmbedPBIreport_id}`);
          if (stored !== null) setChatOpen(stored === "true");
        } catch { /* localStorage may be unavailable */ }
      })
      .catch(() => { if (!cancelled) setOnboardingEnabled(false); });
    return () => { cancelled = true; };
  }, [EmbedPBIreport_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // console.log(EmbedUrl)
  const report = window.report;

  function fn(e) {
    for (var i = tooltip.length; i--; ) {
      tooltip[i].style.left = e.pageX + "px";
      tooltip[i].style.top = e.pageY + "px";
    }
  }

  if (isLoaded) {
    var tooltip;
    tooltip = document.querySelectorAll(".tooltiptext");
    document.addEventListener("mousemove", fn, false);
    viewNotificationsReply();
  }

  /**
   * The bookmark named in the URL, if any.
   *
   * Read from the server rather than sessionStorage so a refresh still lands on
   * the saved view — the URL is the only part of "how I got here" that survives.
   */
  const applyBookmarkRestore = async (activePage) => {
    if (!bookmarkIdFromUrl) return false;

    try {
      const { data } = await apiService.get(apiUrl.aipdBookmarkState(bookmarkIdFromUrl));
      const { pagename: savedPage, filterJson, slicerJson } = data || {};
      if (!savedPage) return false;
      if (activePage.name !== savedPage) {
        console.info("[bookmark] belongs to another page", savedPage, "open:", activePage.name);
        return false;
      }

      console.info("[bookmark] restoring", savedPage, { filterJson, slicerJson });
      restoringFiltersRef.current = true;

      // Page-level filters
      if (filterJson) {
        try {
          const filters = JSON.parse(filterJson);
          if (Array.isArray(filters) && filters.length > 0) {
            await activePage.setFilters(filters);
          }
        } catch (e) {
          console.warn("Could not apply bookmark filters:", e);
        }
      }

      // Apply slicer states with retry logic (visuals may not be initialized yet)
      if (slicerJson) {
        try {
          const slicers = JSON.parse(slicerJson);
          
          const applySlicerWithRetry = async (slicerData, maxRetries = 5, delay = 500) => {
            for (let attempt = 1; attempt <= maxRetries; attempt++) {
              try {
                const visuals = await activePage.getVisuals();
                const visual = visuals.find((v) => v.name === slicerData.visualName);
                if (!visual) return;
                if (slicerData.state) {
                  await visual.setSlicerState(slicerData.state);
                  return;
                }
              } catch (err) {
                if (err.message === "visualConfigIsNotInitialized" && attempt < maxRetries) {
                  await new Promise((r) => setTimeout(r, delay));
                } else {
                  console.warn("[bookmark] Failed to apply slicer:", slicerData.title, err);
                  return;
                }
              }
            }
          };

          for (const slicerData of slicers) {
            await applySlicerWithRetry(slicerData);
          }
        } catch (e) {
          console.warn("Could not apply bookmark slicers:", e);
        }
      }

      // The bookmark is the view the user asked for; the page's last-applied
      // state must not overwrite it when the page-change restore fires.
      bookmarkRestoredPageRef.current = activePage.name;
      await refreshCurrentSlice(activePage);
      console.info("[bookmark] restored", savedPage);
      setTimeout(() => {
        restoringFiltersRef.current = false;
      }, 1500);
      return true;
    } catch (e) {
      console.warn("bookmark-restore error:", e);
      restoringFiltersRef.current = false;
      return false;
    }
  };

  // Filters/slicers a normal (non-bookmark) visit left behind, kept per report +
  // page in the DB so the view follows the account rather than the browser.
  const saveFilterStateTimer = useRef(null);
  const sliceRefreshTimer = useRef(null);
  const restoringFiltersRef = useRef(false);
  const bookmarkRestoredPageRef = useRef(null);
  // What is on screen right now, so a bookmark can be matched on its view rather
  // than just its page.
  const [currentSlice, setCurrentSlice] = useState({ filterJson: null, slicerJson: null });

  // The bookmark, if any, whose saved view is the one on screen.
  const matchingBookmark = useMemo(() => {
    const current = sliceSignature(currentSlice.filterJson, currentSlice.slicerJson);
    const match = saveBookmarkReports.find(
      (item) =>
        item.pagename === pagename &&
        sliceSignature(item.filterJson, item.slicerJson) === current
    );
    console.info("[bookmark] current signature", current, "match:", match?.title || null);
    return match;
  }, [saveBookmarkReports, pagename, currentSlice]);

  const captureSlice = async (activePage) => {
    const filters = await activePage.getFilters();
    const filterJson = filters && filters.length > 0 ? JSON.stringify(filters) : null;

    const visuals = await activePage.getVisuals();
    const slicers = visuals.filter((v) => v.type === "slicer");
    const slicerStates = await Promise.all(
      slicers.map(async (s) => {
        try {
          const state = await s.getSlicerState();
          return { visualName: s.name, title: s.title, state };
        } catch {
          return null;
        }
      })
    );
    const validSlicers = slicerStates.filter(Boolean);
    const slicerJson = validSlicers.length > 0 ? JSON.stringify(validSlicers) : null;

    return { filterJson, slicerJson };
  };

  // Kept in sync on every change, including the ones a restore causes, so the
  // bookmark icon never claims a view that is no longer on screen.
  const refreshCurrentSlice = async (activePage) => {
    try {
      setCurrentSlice(await captureSlice(activePage));
    } catch (e) {
      console.warn("Could not read current slice:", e);
    }
  };

  const saveLastAppliedFilters = async (activePage) => {
    try {
      const { filterJson, slicerJson } = await captureSlice(activePage);
      setCurrentSlice({ filterJson, slicerJson });

      // Restoring raises the same events as a user edit; saving them back would
      // just rewrite what was read.
      if (restoringFiltersRef.current) return;

      // A single slicer click raises several events; only the settled state is
      // worth a round trip.
      clearTimeout(saveFilterStateTimer.current);
      saveFilterStateTimer.current = setTimeout(() => {
        apiService
          .post(apiUrl.aipdReportPageFilterState(), {
            reportId: Number(ReportID),
            pageName: activePage.name,
            filterJson,
            slicerJson,
          })
          .catch((err) => console.warn("Could not save filter state:", err?.message));
      }, 800);
    } catch (e) {
      console.warn("Could not save last applied filters:", e);
    }
  };

  // Restores the last filters/slicers saved for this report+page on a normal (non-bookmark) visit
  const applyLastAppliedFilters = async (activePage) => {
    if (bookmarkRestoredPageRef.current === activePage.name) return;

    restoringFiltersRef.current = true;
    try {
      const response = await apiService.get(
        apiUrl.aipdReportPageFilterState(ReportID, activePage.name)
      );
      const { filterJson, slicerJson } = response.data || {};

      if (filterJson) {
        const filters = JSON.parse(filterJson);
        if (Array.isArray(filters) && filters.length > 0) {
          await activePage.setFilters(filters);
        }
      }

      if (slicerJson) {
        const slicers = JSON.parse(slicerJson);
        
        // Helper to apply a single slicer with retry logic
        const applySlicerWithRetry = async (slicerData, maxRetries = 5, delay = 500) => {
          for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
              const visuals = await activePage.getVisuals();
              const visual = visuals.find((v) => v.name === slicerData.visualName);
              if (!visual) return;
              if (slicerData.state) {
                await visual.setSlicerState(slicerData.state);
                return; // Success
              }
            } catch (err) {
              if (err.message === "visualConfigIsNotInitialized" && attempt < maxRetries) {
                await new Promise((r) => setTimeout(r, delay));
              } else {
                return;
              }
            }
          }
        };

        // Apply all slicers with retry
        for (const slicerData of slicers) {
          await applySlicerWithRetry(slicerData);
        }
      }
    } catch (e) {
      // Silent fail - filters not critical
    } finally {
      await refreshCurrentSlice(activePage);
      // Power BI reports the resulting events a beat later than the awaits above.
      setTimeout(() => {
        restoringFiltersRef.current = false;
      }, 1500);
    }
  };

  /**
   * Put a page back the way the user should find it: the bookmark they clicked
   * if there is one, otherwise whatever they last left on that page.
   *
   * Run from both "loaded" and "pageChanged" because setActive() may or may not
   * raise pageChanged depending on which page the embed opened on, and a restore
   * that runs before that switch settles is discarded.
   */
  const restorePageState = async (page) => {
    const restoredFromBookmark = await applyBookmarkRestore(page);
    if (!restoredFromBookmark) {
      await applyLastAppliedFilters(page);
    }
    await refreshCurrentSlice(page);
  };

  // Clicking a bookmark for the report already on screen only changes the URL:
  // the component stays mounted and "loaded" never fires again, so the restore
  // has to be driven from the route change.
  useEffect(() => {
    if (!isLoaded || !window.report || !bookmarkIdFromUrl) return;

    (async () => {
      try {
        const pages = await window.report.getPages();
        const page = pages.find((p) => p.name === reportnameFetch);
        if (!page) return;
        if (!page.isActive) await page.setActive();
        bookmarkRestoredPageRef.current = null;
        await restorePageState(page);
      } catch (e) {
        console.warn("Could not restore bookmark on navigation:", e);
      }
    })();
  }, [isLoaded, bookmarkIdFromUrl, reportnameFetch]); // eslint-disable-line react-hooks/exhaustive-deps

  const embdData = (embeddedReport) => {    window.report = embeddedReport;
    window.report.on("loaded", async function () {
      var reportPages = await window.report.getPages();
      // Function to apply filters

      // Resolve the default page: the requested page if it's visible,
      // otherwise the first visible page (never a hidden one).
      const getDefaultPageIndex = (pages) => {
        const requestedIndex = pages.findIndex(
          (p) => p.name === `${reportnameFetch}`
        );
        if (requestedIndex !== -1 && pages[requestedIndex].visibility === 0) {
          return requestedIndex;
        }
        const firstVisibleIndex = pages.findIndex((p) => p.visibility === 0);
        return firstVisibleIndex !== -1 ? firstVisibleIndex : 0;
      };

      window.report.getPages().then(async (pages) => {
        const firstPage = pages[getDefaultPageIndex(pages)];
        const pageName = firstPage.name;
        const displayName = firstPage.displayName;
        setPageName(pageName);
        setDisplyName(displayName);

        // Activating a page clears its filters, so it has to finish before the
        // restore runs — the restore is a round trip now and would otherwise lose
        // the race it used to win when it read from localStorage.
        if (!firstPage.isActive) {
          try {
            await firstPage.setActive();
          } catch (e) {
            console.warn("Could not activate default page:", e);
          }
        }

        // Apply bookmark-saved filters/slicers if navigating from a bookmark; otherwise
        // fall back to whatever filters were last applied on a normal visit to this page
        await restorePageState(firstPage);
      });

      window.report.on("pageChanged", async (event) => {
        // Skip while the chatbot is programmatically flipping through pages to
        // export visuals, so that internal navigation doesn't overwrite the
        // page name/title the rest of the UI is showing
        if (isExportingVisualsRef.current) return;
        const currentPage = event.detail.newPage;
        const pageName = currentPage.name;
        const displayName = currentPage.displayName;
        setPageName(pageName);
        setDisplyName(displayName);

        // State is stored per page, so each one is restored as it opens.
        await restorePageState(currentPage);
      });

      window.report.on("dataSelected", async (event) => {
        // Slicer interactions land here rather than "filtersApplied", so persist on
        // this event too to make sure slicer changes survive a page refresh
        try {
          const pages = await window.report.getPages();
          const activePage = pages.find((p) => p.isActive);
          if (activePage) await saveLastAppliedFilters(activePage);
        } catch (e) {
          console.warn("Could not persist filters on selection:", e);
        }
      });

      window.report.on("filtersApplied", async () => {
        try {
          const pages = await window.report.getPages();
          const activePage = pages.find((p) => p.isActive);
          if (activePage) await saveLastAppliedFilters(activePage);
        } catch (e) {
          console.warn("Could not persist filters on change:", e);
        }
      });

      // A slicer click settles a beat after dataSelected fires, so the slice is
      // read again once the visuals have redrawn — this is what keeps the
      // bookmark icon in step with the filters actually on screen.
      window.report.on("rendered", () => {
        clearTimeout(sliceRefreshTimer.current);
        sliceRefreshTimer.current = setTimeout(async () => {
          try {
            const pages = await window.report.getPages();
            const activePage = pages.find((p) => p.isActive);
            if (activePage) await refreshCurrentSlice(activePage);
          } catch (e) {
            console.warn("Could not refresh current slice:", e);
          }
        }, 400);
      });

      window.report.getPages().then(function (pages) {
        const defaultIndex = getDefaultPageIndex(pages);
        // Activation itself is handled above, next to the filter restore that it
        // would otherwise race.
        setcurrentPage(defaultIndex);
      });

      setPages(reportPages);
      seteLoaded(true);
      trackingData();
      exportAllVisualsData();
    });
  };

  const saveReports = async () => {
    if (matchingBookmark) {
      setopenBookmark(false);
      toast.info("This view is already bookmarked.", {
        position: toast.POSITION.BOTTOM_LEFT,
        autoClose: 4000,
      });
      return;
    }

    setSaving(true);
    setopenBookmark(false);

    // Capture current filters and slicers from the embedded report
    let filterJson = null;
    let slicerJson = null;

    try {
      if (window.report) {
        const pages = await window.report.getPages();
        const activePage = pages.find((p) => p.isActive) || pages[0];
        if (activePage) {
          ({ filterJson, slicerJson } = await captureSlice(activePage));
        }
      }
    } catch (err) {
      console.warn("Could not capture filters/slicers:", err);
    }

    // Checked against what was just read, not the tracked state, which lags the
    // events that produced it.
    const signature = sliceSignature(filterJson, slicerJson);
    const duplicate = saveBookmarkReports.find(
      (item) =>
        item.pagename === pagename &&
        sliceSignature(item.filterJson, item.slicerJson) === signature
    );
    if (duplicate) {
      setSaving(false);
      setCurrentSlice({ filterJson, slicerJson });
      toast.info(`This view is already bookmarked as "${decodeURIComponent(duplicate.title)}".`, {
        position: toast.POSITION.BOTTOM_LEFT,
        autoClose: 5000,
      });
      return;
    }

    // Several views of one page can be bookmarked, and a bookmark is deleted by
    // page + title, so each needs a title of its own.
    const baseTitle = `${pageDisplayname}`;
    const samePageBookmarks = saveBookmarkReports.filter((item) => item.pagename === pagename);
    const title =
      samePageBookmarks.length > 0 ? `${baseTitle} (${samePageBookmarks.length + 1})` : baseTitle;

    const data = [
      {
        reportId: parseInt(ReportID),
        pagename: `${pagename}`,
        title,
        filterJson,
        slicerJson,
        // The persona the report is being viewed under, not the caller's default.
        userGroupId: groupIdFromUrl ? parseInt(groupIdFromUrl) : undefined,
      },
    ];

    saveBookmark(data)
      .then(function () {
        GetBookmarkURLReports();
        setSaving(false);
        toast.success(
          `${decodeURIComponent(pageDisplayname)} Added Successfully`,
          {
            position: toast.POSITION.BOTTOM_LEFT,
            autoClose: 6000,
          }
        );
      })
      .catch(function (error) {
        console.log(error);
        alert("Error");
        setSaving(false);
      });
  };

  const removeBookmark = () => {
    setRemoving(true);
    setOpen(false);

    // Delete the bookmark whose saved view is the one on screen, not merely the
    // first one saved for this page.
    const titleToRemove = matchingBookmark?.title || pageDisplayname;

    deleteBookmark(parseInt(ReportID), pagename, titleToRemove)
      .then(() => {
        setRemoving(false);
        GetBookmarkURLReports();
        toast.success(
          `${decodeURIComponent(titleToRemove)} Bookmark Removed Successfully`,
          {
            // Set to 15sec
            position: toast.POSITION.BOTTOM_LEFT,
            autoClose: 6000,
          }
        );
      })

      .catch((error) => {
        alert("Error deleting resource:", error);
      });
  };

  const downloadReportAsPDF = async () => {
    report.print().catch((error) => {
      console.log(error);
    });
  };

  const [isExportingPptx, setIsExportingPptx] = useState(false);

  // Export PowerPoint with embedded live Power BI add-in
  const [isExportingLivePptx, setIsExportingLivePptx] = useState(false);
  const [exportPptxDialogOpen, setExportPptxDialogOpen] = useState(false);
  const [embedWithFilters, setEmbedWithFilters] = useState(true);
  const [liveEmbedUrl, setLiveEmbedUrl] = useState("");
  const [serviceUrl, setServiceUrl] = useState("");
  const [exportMode, setExportMode] = useState("live"); // "live" or "image"

  // Generate the embed URL when dialog opens
  const openExportPptxDialog = async () => {
    setExportPptxDialogOpen(true);
    setLiveEmbedUrl(""); // Reset while loading
    setServiceUrl("");
    try {
      const response = await apiService.get(apiUrl.aipdReportPowerPointEmbedUrl(ReportID, pagename));
      // Handle both response formats
      const embedUrl = response.data?.data?.embedUrl || response.data?.embedUrl;
      const svcUrl = response.data?.data?.serviceUrl || response.data?.serviceUrl;
      if (embedUrl) {
        setLiveEmbedUrl(embedUrl);
      }
      if (svcUrl) {
        setServiceUrl(svcUrl);
      }
    } catch (error) {
      console.error("Failed to get embed URL:", error);
      // Fallback - URL will remain empty and show "Loading..."
    }
  };

  const handleCopyLiveEmbedUrl = () => {
    if (liveEmbedUrl) {
      navigator.clipboard.writeText(liveEmbedUrl);
      toast.success("Embed URL copied to clipboard!", { position: toast.POSITION.BOTTOM_LEFT, autoClose: 2000 });
    }
  };

  const handleCopyServiceUrl = () => {
    if (serviceUrl) {
      navigator.clipboard.writeText(serviceUrl);
      toast.success("Report link copied to clipboard!", { position: toast.POSITION.BOTTOM_LEFT, autoClose: 2000 });
    }
  };

  const handleExportFromDialog = async () => {
    if (exportMode === "image") {
      // Export as image
      setIsExportingLivePptx(true);
      try {
        const exportUrl = apiUrl.aipdReportExportImage(ReportID, {
          pageName: pagename,
          useDefaultValues: false,
          excludeHiddenPages: true,
          currentPageOnly: true,
        });
        
        const response = await apiService.get(exportUrl, {
          responseType: "blob",
        });
        const blob = new Blob([response.data], { type: "image/png" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        let safeName = "";
        try {
          safeName = decodeURIComponent(pageDisplayname || "");
        } catch {
          safeName = pageDisplayname || "";
        }
        safeName = safeName.replace(/[\\/:*?"<>|]+/g, "").trim();

        link.download = `${safeName || "report"}.png`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        setExportPptxDialogOpen(false);
        toast.success("Image exported successfully!", {
          position: toast.POSITION.BOTTOM_LEFT,
          autoClose: 3000,
        });
      } catch (error) {
        console.error(error);
        const backendMessage = await extractBlobErrorMessage(error);
        const friendlyMessage = backendMessage?.includes("disabled on tenant")
          ? "Export to Image isn't available — your Power BI tenant has image export disabled. Contact your admin to enable it."
          : backendMessage || "Could not export report to image. Please try again.";
        setExportPptxDialogOpen(false);
        toast.error(friendlyMessage, {
          position: toast.POSITION.BOTTOM_LEFT,
          autoClose: 8000,
        });
      } finally {
        setIsExportingLivePptx(false);
      }
    } else {
      // Export as live PowerPoint
      handleOpenInPowerPoint();
    }
  };

  const handleOpenInPowerPoint = async () => {
    setIsExportingLivePptx(true);
    try {
      const response = await apiService.get(apiUrl.aipdReportExportLivePptx(ReportID, embedWithFilters ? pagename : null), {
        responseType: "blob",
      });
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      let safeName = "";
      try {
        safeName = decodeURIComponent(pageDisplayname || "");
      } catch {
        safeName = pageDisplayname || "";
      }
      safeName = safeName.replace(/[\\/:*?"<>|]+/g, "").trim();

      link.download = `${safeName || "report"} (Live).pptx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setExportPptxDialogOpen(false);
      toast.success("Downloaded! Open the file in PowerPoint desktop to see live data.", {
        position: toast.POSITION.BOTTOM_LEFT,
        autoClose: 5000,
      });
    } catch (error) {
      console.error(error);
      const backendMessage = await extractBlobErrorMessage(error);
      toast.error(backendMessage || "Could not export live PowerPoint. Please try again.", {
        position: toast.POSITION.BOTTOM_LEFT,
      });
    } finally {
      setIsExportingLivePptx(false);
    }
  };

  // With responseType "blob", axios doesn't parse a JSON error body for us —
  // it arrives as a Blob, so it has to be read/parsed manually.
  const extractBlobErrorMessage = async (error) => {
    try {
      const data = error?.response?.data;
      if (data instanceof Blob) {
        const text = await data.text();
        return JSON.parse(text)?.message;
      }
      return error?.response?.data?.message;
    } catch {
      return null;
    }
  };

  // Mirrors PowerBI's own "Export to PowerPoint" — the live data currently shown
  // in the embedded report is exported server-side via the PowerBI REST API
  const downloadReportAsPptx = async () => {
    setIsExportingPptx(true);
    try {
      const response = await apiService.get(apiUrl.aipdReportExportPptx(ReportID), {
        responseType: "blob",
      });
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Sanitize into a safe, predictable filename (decode %20 etc., strip characters
      // that aren't valid across filesystems) instead of using the raw page title as-is
      let safeName = "";
      try {
        safeName = decodeURIComponent(pageDisplayname || "");
      } catch {
        safeName = pageDisplayname || "";
      }
      safeName = safeName.replace(/[\\/:*?"<>|]+/g, "").trim();

      link.download = `${safeName || "report"}.pptx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error);
      const backendMessage = await extractBlobErrorMessage(error);
      const friendlyMessage = backendMessage?.includes("dedicated capacity")
        ? "Export to PowerPoint isn't available for this report — its PowerBI workspace isn't on a Premium/Fabric capacity. Ask your PowerBI admin to move it to a dedicated capacity to enable this."
        : backendMessage || "Could not export report to PowerPoint. Please try again.";
      toast.error(friendlyMessage, {
        position: toast.POSITION.BOTTOM_LEFT,
        autoClose: 8000,
      });
    } finally {
      setIsExportingPptx(false);

    }
  };

  const trackingData = () => {
    trackReportView(parseInt(ReportID), parseInt(groupIdFromUrl))
      .then(function () {
        console.log("Tracking in progress ...");
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  const onreploadPage = () => {
    console.log("reload");
  };

  // console.log(currentPage);

  const activeCurrentPage = async (index) => {
    var reportPages = await window.report.getPages();
    reportPages[index].setActive();
  };

  // Walks every visible page once (on report load) and caches the exported visuals
  // in state — this is the only place pages get switched, so asking the chatbot a
  // question never re-triggers page navigation.
  const exportAllVisualsData = async () => {
    if (!window.report) return;
    const containerEl = reportContainerRef.current;
    // exportData only returns data for the active page, so each visible page is
    // activated in turn while its visuals are read, then the original page is
    // restored. The container is hidden and pageChanged updates are suppressed
    // so the user doesn't see the report flip between pages while this runs.
    isExportingVisualsRef.current = true;
    setIsExportingVisuals(true);
    if (containerEl) containerEl.style.visibility = "hidden";
    try {
      const pages = await window.report.getPages();
      const visiblePages = pages.filter((p) => p.visibility === 0);
      const originalActivePage = pages.find((p) => p.isActive);
      const allResults = [];

      for (const page of visiblePages) {
        try {
          await page.setActive();
          const visuals = await page.getVisuals();
          const pageResults = await Promise.all(
            visuals
              .filter((v) => v.type !== "slicer" && v.type !== "image" && v.type !== "shape")
              .map(async (v) => {
                try {
                  const exported = await v.exportData(0); // 0 = Summarized
                  return { page: page.displayName || page.name, title: v.title || v.name, type: v.type, csv: exported.data };
                } catch {
                  return null;
                }
              })
          );
          allResults.push(...pageResults.filter(Boolean));
        } catch {
          // skip pages that fail to activate or return visuals
        }
      }

      if (originalActivePage) {
        await originalActivePage.setActive();
      }

      setAllVisualsData(allResults);
    } catch {
      setAllVisualsData([]);
    } finally {
      isExportingVisualsRef.current = false;
      setIsExportingVisuals(false);
      if (containerEl) containerEl.style.visibility = "visible";
    }
  };

  // Passed to the chatbot as getVisuals — just reads the cache, no page switching
  const getVisualsData = async () => allVisualsData;

  return (
    <div>
      <AuthenticatedTemplate>
        {PageLoading && (
          <>
            <NavHeader onclickmenu={false} />
            <SideMenu />
          </>
        )}

        {!PageLoading && (
          <>
            <NavHeader
              title={pageDisplayname}
              guid={EmbedPBIreport_id}
              reportId={ReportID}
              pageName={pagename}
              groupId={groupIdFromUrl}
              onclickmenu={Embedaccess_token !== null ? true : false}
              onreploadPage={onreploadPage}
              pageLoad={Embedaccess_token !== null ? isLoaded : false}
            />
            <SideMenu
              activeCurrentPage={(index) => activeCurrentPage(index)}
              setcurrentPage={setcurrentPage}
              id={ReportID}
              pages={pbiPages}
              name={Embedaccess_token !== null ? "PowerBINav" : ""}
              activeIndexData={currentPage}
              reportnameFetch={reportnameFetch || null}
              Embedaccess_token={Embedaccess_token}
            />
          </>
        )}
        <div id="main">
          {/* #main has a 2rem padding on every side. Only top/right/bottom should
              be cancelled here (so the AI panel reaches those edges) — the left
              padding is the page's normal left gap and must stay intact. overflow
              hidden so this row can never itself become a scroll container that
              drags both columns (and the AI panel's header) along with it —
              each column below scrolls internally instead. */}
          <div style={{ width: "calc(100% + 2rem)", height: "calc(100% + 4rem)", marginTop: "-2rem", position: "relative", overflow: "hidden" }}>
            {isResizingChat && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 9999,
                  cursor: "col-resize",
                  background: "transparent",
                }}
              />
            )}
            <div ref={splitContainerRef} style={{ height: "100%", display: "flex" }}>
            <div
              style={{
                position: "relative",
                height: "100%",
                minHeight: 0,
                flex: "1 1 auto",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                overflowY: "auto",
              }}
            >
            {!isLoading && !iserror && Embedaccess_token !== null && isLoaded && !isExportingVisuals && (
              <div
                style={{
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px",
                  marginBottom: "8px",
                }}
              >
                <AppButton
                  variant="outline"
                  icon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
                  onClick={() => navigate(-1)}
                  style={{ padding: "8px 12px", background: "white" }}
                  title="Go back"
                >
                  Go back
                </AppButton>

                <AppButton
                  variant="fill"
                  icon={<FaPrint />}
                  onClick={downloadReportAsPDF}
                  style={{ padding: "8px", minWidth: "unset" }}
                  title="Print / Save as PDF"
                >
                  Print / Save as PDF
                </AppButton>

                <AppButton
                  variant="fill"
                  icon={<FaFilePowerpoint />}
                  onClick={openExportPptxDialog}
                  disabled={isExportingLivePptx}
                  style={{ 
                    padding: "8px", 
                    minWidth: "unset", 
                    opacity: isExportingLivePptx ? 0.5 : 1,
                    background: "linear-gradient(135deg, #F2C811 0%, #E8B708 100%)",
                  }}
                  title={isExportingLivePptx ? "Exporting…" : "Export to PowerPoint with live, interactive data"}
                >
                  {isExportingLivePptx ? "Exporting…" : "Export to PowerPoint"}
                </AppButton>

                {featureFlags.Bookmark && (
                  <Tooltip title={matchingBookmark ? `Bookmarked as "${decodeURIComponent(matchingBookmark.title)}"` : "Bookmark this view"} arrow>
                    <Fab
                      size="small"
                      color="primary"
                      onClick={() => matchingBookmark ? setOpen(!open) : setopenBookmark(!openBookmark)}
                      style={{ marginLeft: "auto" }}
                      aria-label={matchingBookmark ? "Bookmarked" : "Bookmark this view"}
                    >
                      {matchingBookmark ? <BookmarkIcon sx={{ color: "white" }} /> : <BookmarkBorderIcon sx={{ color: "white" }} />}
                    </Fab>
                  </Tooltip>
                )}
              </div>
            )}

            <div style={{ position: "relative", flex: "1 1 auto", minHeight: 0 }}>
            {!isLoaded && (
              <div
                style={{
                  position: "absolute",
                  background: "white",
                  left: 0,
                  right: 0,
                  top: 0,
                  bottom: 0,
                  zIndex: 9999999,
                }}
                className="pageLoading"
              >
                <FancyReportLoader message="Report is loading, please wait…" />
              </div>
            )}

            {isLoaded && isExportingVisuals && (
              <div
                style={{
                  position: "absolute",
                  background: "white",
                  left: 0,
                  right: 0,
                  top: 0,
                  bottom: 0,
                  zIndex: 9999999,
                }}
                className="pageLoading"
              >
                <FancyReportLoader message="Preparing Report, please wait…" />
              </div>
            )}

            {!isLoading && !iserror && Embedaccess_token !== null && (
              <div ref={reportContainerRef} style={{ position: "relative", height: "100%" }}>
                <PowerBIEmbed
                  embedConfig={{
                    type: "report", // Supported types: report, dashboard, tile, visual, and qna.
                    id: EmbedPBIreport_id,
                    embedUrl: EmbedUrl,
                    accessToken: Embedaccess_token,
                    //pageName: 'ReportSectione5bc25aa0357c917eaaa',
                    pageName: `${
                      reportnameFetch === null ? "" : reportnameFetch
                    }`,
                    tokenType: models.TokenType.Embed, // Use models.TokenType.Aad if you're embedding for your organization.
                    permissions: models.Permissions.All,
                    viewMode: models.ViewMode.View,

                    settings: {
                      navContentPaneEnabled: false,
                      filterPaneEnabled: true,
                      // Disabled: Power BI's own built-in persistence races with our
                      // custom localStorage-based restore below and can wipe it out
                      persistentFiltersEnabled: false,

                      bars: {
                        actionBar: {
                          visible: true,
                        },
                        statusBar: {
                          visible: false,
                        },
                      },
                      panes: {
                        filters: {
                          expanded: false,
                          visible: true,
                        },
                      },
                    },
                  }}
                  eventHandlers={
                    new Map([
                      [
                        "loaded",
                        async function () {
                          console.log("Report loaded");
                        },
                      ],

                      ["dataSelected", function (event) {}],

                      [
                        "rendered",
                        async function () {
                          // console.log("Report rendered");
                          // var reportPages = await window.report.getPages();
                          // setPbireports(reportPages)
                        },
                      ],
                      [
                        "error",
                        function (event) {
                          // console.log(event.detail);
                        },
                      ],
                    ])
                  }
                  cssClassName={"report-style-class"}
                  getEmbeddedComponent={embdData}
                />
              </div>
            )}

            {!isLoading && Embedaccess_token === null && (
              <div className="reportView_pbi">
                <div className="flex flex-col items-center text-center max-w-[420px] px-12 py-10 bg-white border border-rose-100 rounded-2xl shadow-xl">
                  <div className="flex items-center justify-center w-[72px] h-[72px] mb-5 rounded-full text-[#a3172f] bg-gradient-to-br from-rose-100 to-rose-200 animate-pulse">
                    <MdOutlineAccessTimeFilled size={38} />
                  </div>
                  <h3 className="mb-2 text-lg font-bold text-[#253978]">Report Access Expired</h3>
                  <p className="mb-6 text-sm leading-relaxed text-gray-500">
                    Your PowerBI access token has expired. Please try again in a
                    few minutes.
                  </p>
                  <AppButton
                    variant="fill"
                    icon={<MdRefresh size={18} />}
                    onClick={() => window.location.reload()}
                  >
                    Retry
                  </AppButton>
                </div>
              </div>
            )}

            {iserror && <div>There is some error Please try again later </div>}
            </div>
            </div>

            {chatOpen && featureFlags["Ask AI"] && onboardingEnabled && (
              <>
                <div
                  onMouseDown={handleChatResizeStart}
                  onMouseEnter={() => setIsHoveringChatResize(true)}
                  onMouseLeave={() => setIsHoveringChatResize(false)}
                  title="Drag to resize"
                  style={{
                    width: "10px",
                    flexShrink: 0,
                    cursor: "col-resize",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "transparent",
                  }}
                >
                  <div
                    style={{
                      width: "4px",
                      height: "100%",
                      borderRadius: "4px",
                      background: isResizingChat || isHoveringChatResize ? "#94a3b8" : "#e5e7eb",
                      transition: "background 0.15s ease",
                    }}
                  />
                </div>
                <div
                  style={{
                    width: `${chatWidthPct}%`,
                    height: "100%",
                    minHeight: 0,
                    flexShrink: 0,
                    minWidth: 280,
                    borderLeft: "1px solid #e5e7eb",
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  {isLoaded && Embedaccess_token !== null && !isExportingVisuals && (
                    <ReportChatBot
                      reportName={PowerBiReportsName}
                      pageName={pageDisplayname}
                      reportId={parseInt(ReportID)}
                      reportGuid={EmbedPBIreport_id}
                      groupId={groupIdFromUrl ? parseInt(groupIdFromUrl) : null}
                      passtoken={passtoken}
                      getVisuals={getVisualsData}
                      onClose={() => { setChatOpen(false); try { localStorage.setItem(`chatOpen_${EmbedPBIreport_id}`, "false"); } catch {} }}
                    />
                  )}
                </div>
              </>
            )}
            </div>

            {!chatOpen && featureFlags["Ask AI"] && onboardingEnabled && isLoaded && Embedaccess_token !== null && !isExportingVisuals && (
              <AppButton
                onClick={() => { setChatOpen(true); try { localStorage.setItem(`chatOpen_${EmbedPBIreport_id}`, "true"); } catch {} }}
                variant="fill"
                icon={<SmartToyIcon sx={{ fontSize: 18 }} />}
                style={{
                  position: "fixed",
                  right: "calc(20px + var(--comments-panel-offset, 0px))",
                  bottom: 50,
                  zIndex: 9999,
                  background: "var(--app-ai-assistant-gradient, linear-gradient(135deg,#253978,#1a2a5e))",
                  boxShadow: "0 6px 24px rgba(37,57,120,0.45)",
                  borderRadius: "9999px",
                  padding: "10px 16px",
                }}
                title="Ask AI about this report"
              >
                Ask AI
              </AppButton>
            )}

            {Saving && (
              <div
                style={{
                  position: "fixed",
                  background: "rgba(0,0,0,0.8)",
                  width: "100%",
                  height: "100%",
                  top: "0",
                  left: "0",
                  right: "0",
                  bottom: "0",
                  display: "grid",
                  justifyContent: "center",
                  alignItems: "center",
                  color: "white",
                  zIndex: "99999",
                  justifyItems: "center",
                  alignContent: "center",
                }}
              >
                <BarChartLoader label={null} />
                <span>Adding your bookmark...</span>
              </div>
            )}

            {removing && (
              <div
                style={{
                  position: "fixed",
                  background: "rgba(0,0,0,0.8)",
                  width: "100%",
                  height: "100%",
                  top: "0",
                  left: "0",
                  right: "0",
                  bottom: "0",
                  display: "grid",
                  justifyContent: "center",
                  alignItems: "center",
                  color: "white",
                  zIndex: "99999",
                  justifyItems: "center",
                  alignContent: "center",
                }}
              >
                <BarChartLoader label={null} />
                <span>Removing your bookmark...</span>
              </div>
            )}

            <Dialog
              open={open}
              onClose={() => setOpen(!open)}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle
                style={{
                  background: "var(--app-ai-assistant-gradient, linear-gradient(135deg,#253978,#1a2a5e))",
                  color: "#fff",
                  fontWeight: "500",
                }}
                id="alert-dialog-title"
              >
                {"This page is already bookmarked!!"}
              </DialogTitle>
              <DialogContent style={{ marginTop: "16px" }}>
                <DialogContentText id="alert-dialog-description">
                  Do you wish to remove the bookmark?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <AppButton onClick={removeBookmark} autoFocus style={{color:"white"}}>
                  Yes
                </AppButton>

                <AppButton variant="secondary" onClick={() => setOpen(!open)}>
                  No
                </AppButton>
              </DialogActions>
            </Dialog>

            <Dialog
              open={openBookmark}
              onClose={() => setopenBookmark(!openBookmark)}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle
                style={{
                  background: "var(--app-ai-assistant-gradient, linear-gradient(135deg,#253978,#1a2a5e))",
                  color: "#fff",
                  fontWeight: "500",
                }}
                id="alert-dialog-title"
              >
                {"A bookmark will be successfully established for this page"}
              </DialogTitle>
              <DialogContent style={{ marginTop: "16px" }}>
                <DialogContentText id="alert-dialog-description">
                  Please confirm to bookmark this page
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <AppButton onClick={saveReports} autoFocus style={{color:"white"}}>
                  Yes
                </AppButton>

                <AppButton variant="secondary" onClick={() => setopenBookmark(!openBookmark)}>
                  No
                </AppButton>
              </DialogActions>
            </Dialog>

            {/* Export to PowerPoint Dialog - Similar to Power BI's native dialog */}
            <Dialog
              open={exportPptxDialogOpen}
              onClose={() => !isExportingLivePptx && setExportPptxDialogOpen(false)}
              maxWidth="sm"
              fullWidth
              PaperProps={{
                sx: { borderRadius: 2 }
              }}
            >
              <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
                <FaFilePowerpoint style={{ color: "#D24726", fontSize: 24 }} />
                <span style={{ fontWeight: 600 }}>Export to PowerPoint</span>
              </DialogTitle>
              <DialogContent>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ fontWeight: 500, fontSize: 14, marginBottom: 8, display: "block", color: "#333" }}>
                    Choose how to export:
                  </label>
                  <FormControl fullWidth size="small">
                    <Select
                      value={exportMode}
                      onChange={(e) => setExportMode(e.target.value)}
                      MenuProps={{
                        disablePortal: true,
                        PaperProps: {
                          sx: { zIndex: 1500 }
                        }
                      }}
                      sx={{ 
                        borderRadius: 1,
                        "& .MuiSelect-select": { py: 1.5 }
                      }}
                    >
                      <MenuItem value="live">Embed live data</MenuItem>
                      <MenuItem value="image">Export as image</MenuItem>
                    </Select>
                  </FormControl>
                </div>

                {exportMode === "live" ? (
                  <>
                    <DialogContentText sx={{ mb: 2, color: "#666", fontSize: 14 }}>
                      Copy the report URL and paste it into your existing presentation or select Download PowerPoint to create a new presentation.
                    </DialogContentText>

                    <div style={{ marginBottom: 16 }}>
                      <label style={{ fontWeight: 500, fontSize: 14, marginBottom: 8, display: "block", color: "#333" }}>
                        Report page link:
                      </label>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <TextField
                          fullWidth
                          value={serviceUrl || "Loading..."}
                          InputProps={{ 
                            readOnly: true,
                            sx: { 
                              fontSize: 13, 
                              bgcolor: "#f5f5f5",
                              "& input": { 
                                textOverflow: "ellipsis",
                                overflow: "hidden"
                              }
                            }
                          }}
                          size="small"
                        />
                        <Button
                          variant="contained"
                          onClick={handleCopyServiceUrl}
                          disabled={!serviceUrl}
                          sx={{ 
                            bgcolor: "#0d6c5e",
                            "&:hover": { bgcolor: "#0a5a4e" },
                            textTransform: "none",
                            fontWeight: 600,
                            px: 3,
                            minWidth: 80
                          }}
                        >
                          Copy
                        </Button>
                      </div>
                    </div>

                    <div>
                      <label style={{ fontWeight: 500, fontSize: 14, marginBottom: 8, display: "block", color: "#333" }}>
                        Embed link (for PowerPoint add-in):
                      </label>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <TextField
                          fullWidth
                          value={liveEmbedUrl || "Loading..."}
                          InputProps={{ 
                            readOnly: true,
                            sx: { 
                              fontSize: 13, 
                              bgcolor: "#f5f5f5",
                              "& input": { 
                                textOverflow: "ellipsis",
                                overflow: "hidden"
                              }
                            }
                          }}
                          size="small"
                        />
                        <Button
                          variant="contained"
                          onClick={handleCopyLiveEmbedUrl}
                          disabled={!liveEmbedUrl}
                          sx={{ 
                            bgcolor: "#0d6c5e",
                            "&:hover": { bgcolor: "#0a5a4e" },
                            textTransform: "none",
                            fontWeight: 600,
                            px: 3,
                            minWidth: 80
                          }}
                        >
                          Copy
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <DialogContentText sx={{ color: "#666", fontSize: 14 }}>
                    Export the current report page as a PNG image. Note: This feature requires your Power BI tenant to have image export enabled.
                  </DialogContentText>
                )}
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleExportFromDialog}
                  disabled={isExportingLivePptx}
                  startIcon={isExportingLivePptx ? <CircularProgress size={18} color="inherit" /> : (exportMode === "live" ? <FaFilePowerpoint /> : null)}
                  sx={{ 
                    bgcolor: exportMode === "live" ? "#D24726" : "#4CAF50",
                    "&:hover": { bgcolor: exportMode === "live" ? "#b83d20" : "#45a049" },
                    "&.Mui-disabled": { bgcolor: exportMode === "live" ? "#D24726" : "#4CAF50", color: "white", opacity: 0.7 },
                    textTransform: "none",
                    fontWeight: 600,
                    px: 2
                  }}
                >
                  {isExportingLivePptx 
                    ? "Downloading…" 
                    : exportMode === "live" 
                      ? "Download PowerPoint" 
                      : "Download Image"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setExportPptxDialogOpen(false)}
                  disabled={isExportingLivePptx}
                  sx={{ 
                    textTransform: "none",
                    color: "#333",
                    borderColor: "#ccc",
                    "&:hover": { borderColor: "#999", bgcolor: "#f5f5f5" }
                  }}
                >
                  Cancel
                </Button>
              </DialogActions>
            </Dialog>

            <ToastContainer />
          </div>
        </div>
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <Login />
      </UnauthenticatedTemplate>
    </div>
  );
};

export default PowerBiReports;
